import { eyeIcon } from "@excalidraw/excalidraw/components/icons";
import { MainMenu } from "@excalidraw/excalidraw/index";
import React from "react";

import { isDevEnv } from "@excalidraw/common";

import type { Theme } from "@excalidraw/element/types";

import { useSetAtom } from "../app-jotai";
import { LanguageList } from "../app-language/LanguageList";

import { saveDebugState } from "./DebugCanvas";
import { diagramsDialogAtom } from "./DiagramsDialog";
import { historyPanelAtom } from "./HistoryPanel";
import { shareDialogAtom } from "./ShareDialog";

// Injected at build time from excalidraw-app/package.json (see vite.config.mts).
declare const __APP_VERSION__: string;

export const AppMainMenu: React.FC<{
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  theme: Theme | "system";
  refresh: () => void;
  // True when opened via a public share link — hide all account / owner UI so a
  // guest is scoped to this one diagram (the API also blocks it server-side).
  isSharedView?: boolean;
  // True for a read-only (view) share link — strip every content-extraction
  // item (save / export / load / clear / command palette). Note Export and
  // SaveAsImage do NOT self-hide via UIOptions, so they must be gated here.
  isReadOnly?: boolean;
}> = React.memo((props) => {
  const setDiagramsOpen = useSetAtom(diagramsDialogAtom);
  const setHistoryOpen = useSetAtom(historyPanelAtom);
  const setShareOpen = useSetAtom(shareDialogAtom);
  const showAccount = !props.isSharedView;
  const canExtract = !props.isReadOnly;
  return (
    <MainMenu>
      {showAccount && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => setDiagramsOpen(true)}
          className="highlighted"
        >
          Open…
        </MainMenu.Item>
      )}
      {showAccount && (
        <MainMenu.Item icon={eyeIcon} onSelect={() => setShareOpen(true)}>
          Share link
        </MainMenu.Item>
      )}
      {showAccount && (
        <MainMenu.Item icon={eyeIcon} onSelect={() => setHistoryOpen(true)}>
          Version history
        </MainMenu.Item>
      )}
      {canExtract && <MainMenu.DefaultItems.SaveToActiveFile />}
      {canExtract && <MainMenu.DefaultItems.Export />}
      {canExtract && <MainMenu.DefaultItems.SaveAsImage />}
      {canExtract && (
        <MainMenu.DefaultItems.CommandPalette className="highlighted" />
      )}
      <MainMenu.DefaultItems.SearchMenu />
      <MainMenu.DefaultItems.Help />
      {canExtract && <MainMenu.DefaultItems.ClearCanvas />}
      {isDevEnv() && (
        <MainMenu.Item
          icon={eyeIcon}
          onSelect={() => {
            if (window.visualDebug) {
              delete window.visualDebug;
              saveDebugState({ enabled: false });
            } else {
              window.visualDebug = { data: [] };
              saveDebugState({ enabled: true });
            }
            props?.refresh();
          }}
        >
          Visual Debug
        </MainMenu.Item>
      )}
      <MainMenu.Separator />
      <MainMenu.DefaultItems.Preferences />
      <MainMenu.DefaultItems.ToggleTheme allowSystemTheme theme={props.theme} />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
      <MainMenu.Separator />
      <MainMenu.ItemCustom>
        <div
          style={{
            width: "100%",
            textAlign: "left",
            fontSize: "0.6875rem",
            opacity: 0.5,
            padding: "2px 0",
            userSelect: "text",
          }}
          title="App version currently running"
        >
          v{__APP_VERSION__}
        </div>
      </MainMenu.ItemCustom>
    </MainMenu>
  );
});
